import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A continuously scrolling image marquee.
 *
 * Unlike the text `marquee-component`, motion here is constant — it never slows
 * or pauses on hover. It clones the image set enough times to fill the viewport,
 * then duplicates the whole strip once so the loop is seamless. The animation
 * duration is derived from the total content width so the on-screen scroll speed
 * (pixels per second) stays consistent no matter how many images are uploaded.
 */
class ImageMarquee extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  #marqueeWidth = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.refs.marqueeItems.length === 0) return;
    this.#init();
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
  }

  async #init() {
    const { numberOfCopies, itemsWidth } = await this.#queryNumberOfCopies();
    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(itemsWidth * numberOfCopies);
  }

  get #clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  /**
   * Translate the travelled distance (one full content width, in px) into an
   * animation duration so the linear speed matches `data-speed` (px per second).
   */
  #setSpeed(contentWidth) {
    const pixelsPerSecond = Number(this.getAttribute('data-speed')) || 40;
    const duration = contentWidth > 0 ? contentWidth / pixelsPerSecond : 20;
    this.style.setProperty('--marquee-speed', `${duration}s`);
  }

  #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        return setTimeout(() => resolve({ numberOfCopies: 1, itemsWidth: 0, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: itemsWidth } = firstEntry.boundingClientRect;
          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: itemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / itemsWidth),
              itemsWidth,
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );

      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;
    if (!marqueeItems[0]) return;
    for (let i = 0; i < numberOfCopies - 1; i++) {
      content.appendChild(marqueeItems[0].cloneNode(true));
    }
  }

  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const itemsToRemove = Math.min(numberOfCopies, content.children.length - 1);
    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }

  #duplicateContent() {
    this.#clonedContent?.remove();
    const clone = this.refs.content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this.refs.wrapper.appendChild(clone);
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { numberOfCopies, itemsWidth, isHorizontalResize } = await this.#queryNumberOfCopies();
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    if (numberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(numberOfCopies - currentNumberOfCopies);
    } else if (numberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - numberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(itemsWidth * numberOfCopies);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();
    requestAnimationFrame(() => {
      for (const animation of animations) animation.currentTime = 0;
    });
  }
}

if (!customElements.get('image-marquee')) {
  customElements.define('image-marquee', ImageMarquee);
}
